import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    IconButton,
    List,
    Skeleton,
    ListItem,
    ListItemIcon,
    ListItemText,
    Pagination,
    useTheme,
    Paper
} from '@mui/material';
import {
    ArrowBack,
    DoneAll,
    CheckCircle,
    Notifications as NotificationsIcon,
    Refresh
} from '@mui/icons-material';
import { Layout } from '@layout/Layout';
import { NotificationItem, NotificationFilters } from '@/components/Notifications';
import useNotificationStore, { Notification } from '@/store/NotificationStore';

const NotificationsPage: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isDark = theme.palette.mode === 'dark';

    const {
        notifications,
        unreadCount,
        pagination,
        loading,
        filters,
        fetchNotifications,
        markAsRead,
        markAllAsRead,
        dismissNotification,
        setFilters,
        resetFilters
    } = useNotificationStore();

    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    // Fetch notifications on mount and when filters/page change
    useEffect(() => {
        fetchNotifications(currentPage, pageSize);
    }, [currentPage, filters, fetchNotifications]);

    const handleNotificationClick = (notification: Notification) => {
        if (!notification.is_read) {
            markAsRead(notification.id);
        }
        if (notification.action_url) {
            navigate(notification.action_url);
        }
    };

    const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
        setCurrentPage(page);
    };

    const handleMarkAllAsRead = () => {
        markAllAsRead();
    };

    const handleRefresh = () => {
        fetchNotifications(currentPage, pageSize);
    };

    return (
        <Layout>
            <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1200, mx: 'auto' }}>
                {/* Page Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 3,
                        flexWrap: 'wrap',
                        gap: 2
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton
                            onClick={() => navigate(-1)}
                            sx={{
                                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                                '&:hover': {
                                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'
                                }
                            }}
                        >
                            <ArrowBack />
                        </IconButton>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                Notifications
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                            onClick={handleRefresh}
                            disabled={loading}
                            sx={{
                                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)'
                            }}
                        >
                            <Refresh />
                        </IconButton>
                        {unreadCount > 0 && (
                            <Button
                                variant="outlined"
                                startIcon={<DoneAll />}
                                onClick={handleMarkAllAsRead}
                                sx={{ textTransform: 'none' }}
                            >
                                Mark all as read
                            </Button>
                        )}
                    </Box>
                </Box>

                {/* Filters */}
                <Box sx={{ mb: 3 }}>
                    <NotificationFilters
                        filters={filters}
                        onFilterChange={setFilters}
                        onReset={resetFilters}
                    />
                </Box>

                {/* Notifications List */}
                <Paper
                    sx={{
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.02)' : theme.palette.background.paper,
                        borderRadius: 2,
                        border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}`,
                        overflow: 'hidden'
                    }}
                >
                    {loading ? (
                        <List sx={{ p: 0 }}>
                            {[1, 2, 3, 4, 5].map(i => (
                                <ListItem key={i} sx={{ py: 2, px: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                    <ListItemIcon sx={{ minWidth: 44 }}>
                                        <Skeleton variant="circular" width={40} height={40} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={<Skeleton variant="text" width="70%" />}
                                        secondary={<Skeleton variant="text" width="40%" />}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    ) : notifications.length === 0 ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                py: 8,
                                px: 2
                            }}
                        >
                            <CheckCircle sx={{ fontSize: 64, color: '#4CAF50', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                                All caught up!
                            </Typography>
                            <Typography variant="body2" color="text.disabled" textAlign="center">
                                You have no notifications
                                {filters.isRead !== undefined || filters.priority || filters.alertType
                                    ? ' matching your filters'
                                    : ''}
                            </Typography>
                            {(filters.isRead !== undefined || filters.priority || filters.alertType) && (
                                <Button
                                    variant="text"
                                    onClick={resetFilters}
                                    sx={{ mt: 2, textTransform: 'none' }}
                                >
                                    Clear filters
                                </Button>
                            )}
                        </Box>
                    ) : (
                        <>
                            <List sx={{ p: 0 }}>
                                {notifications.map((notification, index) => (
                                    <NotificationItem
                                        key={notification.id}
                                        notification={notification}
                                        onMarkAsRead={markAsRead}
                                        onDismiss={dismissNotification}
                                        onClick={handleNotificationClick}
                                        showDivider={index < notifications.length - 1}
                                    />
                                ))}
                            </List>

                            {/* Pagination */}
                            {pagination && pagination.totalPages > 1 && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        py: 2,
                                        borderTop: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}`
                                    }}
                                >
                                    <Pagination
                                        count={pagination.totalPages}
                                        page={currentPage}
                                        onChange={handlePageChange}
                                        color="primary"
                                        showFirstButton
                                        showLastButton
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </Paper>

                {/* Info Text */}
                {!loading && notifications.length > 0 && pagination && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', textAlign: 'center', mt: 2 }}
                    >
                        Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, pagination.totalItems)} of {pagination.totalItems} notifications
                    </Typography>
                )}
            </Box>
        </Layout>
    );
};

export default NotificationsPage;
